// «Agrega accesorios»: «Cambia por un kit», regalos, catálogo de extras y resumen con los montos
// del servidor; las piezas de Promociones van en sus lugares. Sin teléfono ni kit no pasa (REQ-80).
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { ApiError } from '@/api/errors';
import { Button, ConfirmDialog, Loading } from '@/kit';
import {
    useCatalog,
    useReleaseProduct,
    useSelectProduct,
    useWizardSession,
    WIZARD_KEYS,
} from '../api/useWizard';
import {
    QUOTE_DISCARDED_MESSAGE,
    QUOTE_DISCARD_WARNING,
    quoteDiscarded,
} from '../quoteInvalidation';
import { canContinue, continueBlocker, descriptionsBySku } from '../cartLines';
import { continueGateLinesOf } from '../saleCharge';
import {
    anchorLineOf,
    extraCountsBySku,
    extraLinesOfSku,
    kitSwapsFor,
    kitSwapCapChange,
    type KitSwapCapChange,
    latestExtraLineOf,
    saleSummaryOf,
    financingSplitOf,
    kitPieceNamesOf,
    lineVariantsBySku,
    extraLinesOf,
} from '../buildSale';
import { needsCatalogReload } from '../lineErrors';
import { IntentKeys } from '../intentKeys';
import { isTransientRequestError } from '../deviceEnrollment';
import { earliestExpiringLineOf } from '../reservationClock';
import {
    hasExpiredPhone,
    hasLivePhone,
    hasLivePhoneNow,
    expiredExtraLinesOf,
    extraExpiredMessage,
    PHONE_EXPIRED_MESSAGE,
} from '../phoneReservation';
import { isReferredWithExtras } from '../externalReferral';
import {
    LINE_CHANGE_ACTION_LABEL,
    lineChangeAction,
    reservationErrorMessage,
    swapErrorMessage,
} from '../reservationErrors';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';
import { catalogForSession, isSureKeepConfirmed, summaryForSession } from '../sureKeepAvailability';
import {
    BuildSaleSummaryBar,
    BuildSaleSummaryPanel,
    type SummaryReservation,
} from '../components/BuildSaleSummary';
import { BuildSaleHeading } from '../components/BuildSaleHeading';
import { ExtrasCatalog } from '../components/ExtrasCatalog';
import { SaveAndExitButton, WizardBackLink } from '../components/WizardFootNav';
import { previousStepSlug, stepAfterCheckout } from '../steps';
import {
    anchorGiftLinesOf,
    saleQuoteStateOf,
    type AnchorGiftLine,
    type CardSaleBenefits,
    type OnSaleChanged,
    type SaleQuoteState,
    type SessionRefreshContext,
} from '../promotionSlots';
import type { CatalogItem, ReservedLine, SalePromotionRef, WizardSession } from '../types';
import type { LineSlots } from '../components/LineLabels';
import { pendingRevalidationLineIds } from '../promotionRevalidation';
import { LostLinesNotice } from '../components/LostLinesNotice';
import { lostLineViewsOf } from '../lostLines';

/** Lo que recibe una pieza de Promociones montada junto al equipo. */
export interface AnchorSlotContext extends SessionRefreshContext {
    anchorLine: ReservedLine;
    /** Promo de venta principal de la sesión; ausente con «Ninguna». */
    salePromotion?: SalePromotionRef;
    /** Todas las promos de venta fijadas; en sesiones viejas falta. */
    salePromotions?: SalePromotionRef[];
    /** Líneas de extras de la venta, con sus promos. */
    extraLines?: ReservedLine[];
    /** Regalos vivos ligados a la ancla. */
    anchorGiftLines: AnchorGiftLine[];
    /** Con `charged` la promo, el regalo y el código ya no se cambian. */
    quoteState: SaleQuoteState;
    /** `true` si se puede escribir; con cotización viva antes pregunta si se descarta. */
    confirmQuoteDiscard: () => Promise<boolean>;
}

/** Bloquea «Continuar» mientras `pending`; `reason` se muestra bajo el botón. */
export type SetSelectionPending = (pending: boolean, reason?: string) => void;

export interface GiftSlotContext extends AnchorSlotContext {
    /** `true` mientras falte elegir regalo o la promo no esté confirmada (REQ-133). */
    setSelectionPending: SetSelectionPending;
}

export interface GiftsSlotContext extends GiftSlotContext {
    /** Lleva a «Elige el teléfono» cuando el regalo marcado ya no se puede elegir aquí. */
    onChangeInPhoneStep: () => void;
}

export interface PhonePromotionsSlotContext extends AnchorSlotContext {
    /** Lleva a «Elige el teléfono», donde se cambian las promos y el código. */
    onChangeInPhoneStep: () => void;
}

/** Lugares para las piezas de Promociones (REQ-134); sin pieza no dejan hueco ni bloquean. */
export interface BuildSaleSlots {
    /** Sin selector: siembra y reaplica lo apartado siempre que haya ancla. */
    promotionsSync?: (ctx: GiftSlotContext) => ReactNode;
    /** «Regalos» arriba del catálogo; bloquea mientras falte elegir. */
    gifts?: (ctx: GiftsSlotContext) => ReactNode;
    /** Promos y código del teléfono en solo lectura. */
    phonePromotions?: (ctx: PhonePromotionsSlotContext) => ReactNode;
    /** «Promos del kit (N) · Ver»; solo con un kit como ancla. */
    kitPromotions?: (ctx: GiftSlotContext) => ReactNode;
    /** Aparta y suelta el descuento de accesorios; pinta solo su motivo si no se pudo. */
    accessoryDiscount?: (ctx: AnchorSlotContext) => ReactNode;
    /** ✕ de un regalo en el resumen: quita su promo. */
    removeGift?: (gift: AnchorGiftLine, ctx: AnchorSlotContext) => Promise<void>;
    /** Promos de los accesorios ya en la venta, arriba de los extras. */
    accessoryPromotions?: (ctx: AnchorSlotContext) => ReactNode;
    /** Precio del kit con promo, el mismo que pinta «Elige el teléfono». */
    kitBenefits?: (item: CatalogItem) => CardSaleBenefits;
    /** Burbujas propias y precio por unidad con descuento de cada extra. */
    extraBenefits?: (item: CatalogItem) => CardSaleBenefits;
    /** «Después en la venta»: la pieza pinta su encabezado y nada si no hay qué mostrar. */
    afterSale?: ReactNode;
    /** Nombre de un regalo en el resumen; sin pieza se usa el del catálogo. */
    giftName?: (gift: { sku: string; name: string }) => ReactNode;
    /** Nombre de la promo del equipo en el resumen; sin pieza, «Promoción». */
    promotionLabel?: LineSlots['promotionLabel'];
    /** Se llama después de agregar, quitar o cambiar líneas con éxito. */
    onSaleChanged?: OnSaleChanged;
    /** Lo que bajan las promos solo SureKeep; con él el resumen muestra «De contado $X». */
    sureKeepOnlyDiscountCents?: (session: WizardSession) => number;
}

/** Quién bloquea «Continuar»: la reaplicación, los regalos o el selector del kit. */
type PendingKey = 'sync' | 'gift' | 'sale';

type PendingBy = { anchorId: string } & Partial<Record<PendingKey, string | true>>;

/** Un aviso por pieza; sin cambio real no toca el estado y vale solo para su ancla. */
function pendingSettersFor(
    anchorId: string | undefined,
    setPendingBy: (update: (prev: PendingBy | undefined) => PendingBy | undefined) => void,
): Record<PendingKey, SetSelectionPending> {
    const setterFor =
        (key: PendingKey): SetSelectionPending =>
        (pending, reason) => {
            if (anchorId === undefined) return;
            const value = pending ? (reason ?? true) : undefined;
            setPendingBy((prev) => {
                const base = prev?.anchorId === anchorId ? prev : { anchorId };
                return base === prev && base[key] === value ? prev : { ...base, [key]: value };
            });
        };
    return { sync: setterFor('sync'), gift: setterFor('gift'), sale: setterFor('sale') };
}

/** Bloqueos de las piezas por ancla, con avisos estables mientras la ancla no cambie. */
function usePendingByAnchor(anchorId: string | undefined) {
    const [pendingBy, setPendingBy] = useState<PendingBy>();
    const setters = useMemo(() => pendingSettersFor(anchorId, setPendingBy), [anchorId]);
    return { pendingBy, setters };
}

const PHONE_CHECK_MS = 15_000;

/** Hora actual que se refresca cada `ms`, para notar un apartado que vence sin otra señal. */
function useNow(ms: number): number {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => {
            setNow(Date.now());
        }, ms);
        return () => {
            clearInterval(id);
        };
    }, [ms]);
    return now;
}

/** `busy` = otro apartado seguía en vuelo y este clic se ignora. */
type ReserveOutcome =
    | { status: 'reserved' }
    | { status: 'busy' }
    | { status: 'failed'; error: unknown };

export function BuildSaleStep({
    sessionId,
    slots = {},
}: {
    sessionId: string;
    slots?: BuildSaleSlots;
}) {
    const navigate = useNavigate();
    const { data: session, refresh: refreshSession } = useWizardSession(sessionId);
    const { data: rawCatalog, isLoading, isError, refetch } = useCatalog(sessionId);
    const sureKeep = isSureKeepConfirmed(session);
    const catalog = useMemo(() => catalogForSession(rawCatalog, sureKeep), [rawCatalog, sureKeep]);
    const selectProduct = useSelectProduct(sessionId);
    // Un solo apartado en vuelo: el doble clic en un extra o en el kit no aparta dos unidades.
    const reservingRef = useRef(false);
    const [intentKeys] = useState(() => new IntentKeys());
    const releaseProduct = useReleaseProduct(sessionId);
    const queryClient = useQueryClient();
    // Respuesta pendiente del aviso de descartar la cotización.
    const [discardPrompt, setDiscardPrompt] = useState<(ok: boolean) => void>();

    const anchor = session ? anchorLineOf(session) : undefined;
    const anchorId = anchor?.lineId;
    const { pendingBy, setters } = usePendingByAnchor(anchorId);
    const { sync: setSyncPending, gift: setGiftPending, sale: setSalePending } = setters;
    // Cambio de tope al pasar a kit, para la línea corta del resumen; vale para la ancla nueva.
    const [capChange, setCapChange] = useState<{ sku: string; change: KitSwapCapChange }>();
    // Regalo cuyo ✕ sigue en camino: su renglón gira y no sale otro PUT.
    const [removingGiftKey, setRemovingGiftKey] = useState<string>();
    // «Continuar» gira mientras carga la siguiente pantalla.
    const [leaving, setLeaving] = useState(false);

    const items = useMemo(() => catalog?.items ?? [], [catalog]);
    const descriptions = useMemo(() => descriptionsBySku(items), [items]);
    const now = useNow(PHONE_CHECK_MS);
    const phoneLive = session !== undefined && hasLivePhone(session, now);
    const phoneExpired = session !== undefined && hasExpiredPhone(session, now);
    // La venta derivada ya soltó el teléfono: no hay teléfono que vigilar.
    const cashOnly = session !== undefined && isReferredWithExtras(session);
    // Si el teléfono vence o se suelta con la pantalla abierta, se vuelve a elegirlo; los extras se quedan.
    const hadLivePhone = useRef(false);
    useEffect(() => {
        if (phoneLive) {
            hadLivePhone.current = true;
            return;
        }
        if (cashOnly || !(hadLivePhone.current || phoneExpired)) return;
        hadLivePhone.current = false;
        toast.warning(PHONE_EXPIRED_MESSAGE);
        void navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: 'product' },
        });
    }, [phoneLive, phoneExpired, cashOnly, navigate, sessionId]);

    // Un extra vencido se quita de la venta con aviso; se suelta una sola vez por línea.
    const expiredExtras =
        session !== undefined && phoneLive ? expiredExtraLinesOf(session, now) : [];
    const expiredKey = expiredExtras.map((l) => l.lineId).join('|');
    const releasedExpired = useRef(new Set<string>());
    const releaseExpired = useRef<(lines: ReservedLine[]) => void>(() => undefined);
    useEffect(() => {
        releaseExpired.current = (lines) => {
            const fresh = lines.filter((l) => !releasedExpired.current.has(l.lineId));
            if (fresh.length === 0) return;
            for (const l of fresh) {
                releasedExpired.current.add(l.lineId);
                toast.warning(
                    extraExpiredMessage(
                        catalogDisplayName({ sku: l.sku, ...l.product }, descriptions),
                    ),
                );
            }
            void (async () => {
                for (const l of fresh) {
                    await releaseProduct.mutateAsync({ lineId: l.lineId }).catch(() => undefined);
                }
                void slots.onSaleChanged?.({ kind: 'lines' });
            })();
        };
    });
    useEffect(() => {
        if (expiredKey !== '') releaseExpired.current(expiredExtras);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- la llave resume las líneas vencidas
    }, [expiredKey]);

    if (!session) return <Loading size="lg" />;

    const summary = summaryForSession(saleSummaryOf(session, descriptions), sureKeep);
    // De contado no van las promos ni los códigos que solo aplican con SureKeep.
    const cashTotalCents = summary.totalCents + (slots.sureKeepOnlyDiscountCents?.(session) ?? 0);
    const kits = kitSwapsFor(items, anchor);
    const firstLine = earliestExpiringLineOf(session);
    const reservation: SummaryReservation | undefined =
        firstLine === undefined
            ? undefined
            : {
                  expiresAt: firstLine.expiresAt,
                  ...(firstLine.lineId !== anchor?.lineId && {
                      firstToExpireName: catalogDisplayName(
                          { sku: firstLine.sku, ...firstLine.product },
                          descriptions,
                      ),
                  }),
              };
    const counts = extraCountsBySku(session);
    const lines = continueGateLinesOf(session);
    const ownPending = pendingBy?.anchorId === anchorId ? pendingBy : undefined;
    const pendingReasons = [ownPending?.gift, ownPending?.sale, ownPending?.sync];
    const revalidating = pendingRevalidationLineIds(session).length > 0;
    const giftBlocks = revalidating || pendingReasons.some((r) => r !== undefined);
    // Motivo bajo «Continuar» cuando lo frenan las promos; el regalo manda.
    const slotReason = pendingReasons.find((r): r is string => typeof r === 'string');
    const promotionsReason = slotReason ?? (revalidating ? 'Confirmando promociones…' : undefined);
    // La venta derivada ya soltó el teléfono: solo queda cobrar sus extras.
    const referred = isReferredWithExtras(session);
    const ready = (cashOnly || phoneLive) && canContinue(lines) && !giftBlocks;
    const blocker = continueBlocker(lines);
    const busy = selectProduct.isPending || releaseProduct.isPending;

    // Promo, regalo o código con cotización viva: se descarta como al cambiar productos (DEC-48).
    function confirmQuoteDiscard(): Promise<boolean> {
        const state = session ? saleQuoteStateOf(session) : 'open';
        if (state === 'open') return Promise.resolve(true);
        if (state === 'charged') return Promise.resolve(false);
        return new Promise((resolve) => {
            setDiscardPrompt(() => resolve);
        });
    }
    const answerDiscard = (ok: boolean): void => {
        discardPrompt?.(ok);
        setDiscardPrompt(undefined);
    };

    // Una pieza que falla al recibir el aviso no rompe el paso.
    const notifyLinesChanged = (): void => {
        try {
            void slots.onSaleChanged?.({ kind: 'lines' });
        } catch {
            // La pieza de Promociones maneja sus propios errores.
        }
    };
    const slotContextOf = (slotLine: ReservedLine): AnchorSlotContext => ({
        anchorLine: slotLine,
        ...(session.salePromotion !== undefined && { salePromotion: session.salePromotion }),
        ...(session.salePromotions !== undefined && { salePromotions: session.salePromotions }),
        extraLines: extraLinesOf(session),
        anchorGiftLines: anchorGiftLinesOf(session),
        quoteState: saleQuoteStateOf(session),
        confirmQuoteDiscard,
        refreshSession: async (): Promise<void> => {
            const before = session;
            await refreshSession();
            const after = queryClient.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId));
            if (after && quoteDiscarded(before, after)) toast.info(QUOTE_DISCARDED_MESSAGE);
        },
    });
    const anchorContext = anchor && slotContextOf(anchor);

    /** Avisa el error con el botón de su acción; `retry` repite la misma operación. */
    const reportError = (
        error: unknown,
        retry: () => void,
        message = reservationErrorMessage(error),
    ): void => {
        const action = lineChangeAction(error);
        if (action === undefined) {
            toast.error(message);
        } else {
            const onClick =
                action === 'retry'
                    ? retry
                    : () => {
                          goToStep(action === 'delivery' ? stepAfterCheckout(session) : 'product');
                      };
            toast.error(message, { action: { label: LINE_CHANGE_ACTION_LABEL[action], onClick } });
        }
        // Un fallo puede dejar la línea PENDING o RELEASING: releer la sesión lo muestra.
        void refreshSession();
        if (needsCatalogReload(error)) void refetch();
    };

    /** Aparta con un solo apartado en vuelo; el reintento de la misma intención reusa la llave. */
    const reserveLine = async (sku: string, replacesLineId?: string): Promise<ReserveOutcome> => {
        if (reservingRef.current) return { status: 'busy' };
        reservingRef.current = true;
        const intent = `${sku}|${replacesLineId ?? ''}`;
        try {
            await selectProduct.mutateAsync({
                sku,
                qty: 1,
                ...(replacesLineId !== undefined ? { replacesLineId } : {}),
                idempotencyKey: intentKeys.keyFor(intent),
            });
            intentKeys.settle(intent);
            return { status: 'reserved' };
        } catch (error) {
            // Solo un fallo pasajero reusa la llave; un rechazo del silo deja intentar de nuevo.
            if (!isTransientRequestError(error)) intentKeys.settle(intent);
            return { status: 'failed', error };
        } finally {
            reservingRef.current = false;
        }
    };

    /** Aparta una pieza; desde el modal de variantes avisa a Promociones una vez, al cerrar. */
    const addExtra = async (sku: string, notify = true): Promise<void> => {
        const outcome = await reserveLine(sku);
        if (outcome.status === 'failed') {
            reportError(outcome.error, () => void addExtra(sku, notify));
        }
        if (outcome.status !== 'reserved') return;
        if (notify) notifyLinesChanged();
    };

    /** Suelta la pieza más reciente según la sesión en caché, así piezas seguidas no chocan. */
    const removeExtra = async (sku: string, notify = true): Promise<void> => {
        const fresh =
            queryClient.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId)) ?? session;
        const latest = latestExtraLineOf(fresh, sku);
        if (latest === undefined) return;
        try {
            await releaseProduct.mutateAsync({ lineId: latest.lineId });
        } catch (error) {
            reportError(error, () => void removeExtra(sku, notify));
            return;
        }
        if (notify) notifyLinesChanged();
    };

    // «✕»: suelta una por una las líneas del SKU; no hay endpoint que las suelte juntas.
    const removeAllOfExtra = async (sku: string): Promise<void> => {
        try {
            for (const l of extraLinesOfSku(session, sku)) {
                await releaseProduct.mutateAsync({ lineId: l.lineId });
            }
        } catch (error) {
            reportError(error, () => void removeAllOfExtra(sku));
            return;
        }
        notifyLinesChanged();
    };

    /** Cambia la ancla en una sola llamada; Promociones reaplica lo apartado sobre la nueva. */
    const swapAnchor = async (
        sku: string,
        replacesLineId: string,
        change?: KitSwapCapChange,
    ): Promise<void> => {
        const outcome = await reserveLine(sku, replacesLineId);
        if (outcome.status === 'failed') {
            const { error } = outcome;
            // La línea ya cambió en otro intento: basta con releer la venta.
            if (error instanceof ApiError && error.code === 'LINE_NOT_FOUND') {
                void refreshSession();
                return;
            }
            reportError(
                error,
                () => void swapAnchor(sku, replacesLineId, change),
                swapErrorMessage(error),
            );
        }
        if (outcome.status !== 'reserved') return;
        setCapChange(change === undefined ? undefined : { sku, change });
        notifyLinesChanged();
    };

    // Teléfono → kit (REQ-84, REQ-132): sin aviso previo; si cambia el tope, lo dice el resumen.
    const swapToKit = async (sku: string): Promise<void> => {
        const kit = kits.find((k) => k.sku === sku);
        if (anchor === undefined || kit === undefined) return;
        const change = sureKeep ? kitSwapCapChange(session, kit.cashPriceCents) : undefined;
        await swapAnchor(kit.sku, anchor.lineId, change);
    };

    // Kit → su teléfono, con el mismo cambio de ancla.
    const kitPhoneSku = anchor?.components.find((c) => c.category === 'PHONE')?.sku;
    const removeKit =
        anchor !== undefined && kitPhoneSku !== undefined && !referred
            ? (): void => {
                  void swapAnchor(kitPhoneSku, anchor.lineId);
              }
            : undefined;

    // Pieza que Zentral se llevó: se suelta para elegir otra.
    const removeLost = async (lineId: string): Promise<void> => {
        try {
            await releaseProduct.mutateAsync({ lineId });
        } catch (error) {
            reportError(error, () => void removeLost(lineId));
            return;
        }
        notifyLinesChanged();
    };

    const goToStep = (
        step:
            | 'product'
            | 'payment-method'
            | 'quote'
            | 'referrals'
            | 'delivery'
            | 'payment'
            | 'welcome',
    ): void => {
        void navigate({ to: '/wizard/s/$sessionId/$step', params: { sessionId, step } });
    };

    const continueButton = (
        <Button
            disabled={!ready}
            isLoading={leaving}
            rightIcon={<ArrowRight className="size-4" aria-hidden />}
            onClick={() => {
                if (!cashOnly && !hasLivePhoneNow(session)) {
                    toast.warning(PHONE_EXPIRED_MESSAGE);
                    goToStep('product');
                    return;
                }
                setLeaving(true);
                void Promise.resolve(
                    navigate({
                        to: '/wizard/s/$sessionId/$step',
                        params: { sessionId, step: referred ? 'payment' : 'payment-method' },
                    }),
                ).finally(() => {
                    setLeaving(false);
                });
            }}
        >
            Continuar
        </Button>
    );

    // Por qué «Continuar» no avanza; se pinta junto al botón.
    const continueReason =
        blocker !== undefined ? (
            <p role="status" className="text-[12px] text-warning">
                {blocker}
            </p>
        ) : promotionsReason !== undefined ? (
            <p role="status" className="text-[12px] text-muted-foreground">
                {promotionsReason}
            </p>
        ) : null;

    const isKit = anchor !== undefined && anchor.components.length > 0;
    const releasing = releaseProduct.isPending ? releaseProduct.variables : undefined;
    const releasingLineId = releasing && 'lineId' in releasing ? releasing.lineId : undefined;
    const releasingSku = extraLinesOf(session).find((l) => l.lineId === releasingLineId)?.sku;
    const removingKey =
        isKit && selectProduct.isPending && selectProduct.variables.replacesLineId === anchorId
            ? 'kit'
            : (removingGiftKey ?? releasingSku);
    const summaryProps = {
        summary,
        continueButton,
        continueReason,
        cashTotalCents,
        financing: financingSplitOf(session),
        showFinancing: sureKeep,
        referred,
        reservation,
        onReselect: referred
            ? undefined
            : () => {
                  goToStep('product');
              },
    };
    const ownCapChange = capChange?.sku === anchor?.sku ? capChange?.change : undefined;
    const removeGift =
        anchorContext && slots.removeGift
            ? (gift: { key: string }): void => {
                  const line = anchorGiftLinesOf(session).find((g) => g.lineId === gift.key);
                  if (!line || removingGiftKey !== undefined) return;
                  setRemovingGiftKey(gift.key);
                  void Promise.resolve(slots.removeGift?.(line, anchorContext)).finally(() => {
                      setRemovingGiftKey(undefined);
                  });
              }
            : undefined;

    return (
        <div className="flex w-full max-w-6xl flex-col pb-28 lg:pb-0">
            <header className="mb-3 flex items-start justify-between gap-4">
                <BuildSaleHeading referred={referred} />
                <div className="flex flex-none items-center gap-4 pt-1">
                    {!referred && (
                        <WizardBackLink
                            sessionId={sessionId}
                            step={previousStepSlug('build-sale', session) ?? 'product'}
                        />
                    )}
                    <SaveAndExitButton />
                </div>
            </header>

            <div className="mb-3 empty:hidden">
                <LostLinesNotice
                    lines={lostLineViewsOf(session, descriptions)}
                    onChoosePhone={() => {
                        goToStep('product');
                    }}
                    onRemove={(lineId) => void removeLost(lineId)}
                    {...(releaseProduct.isPending &&
                        'lineId' in releaseProduct.variables && {
                            removingLineId: releaseProduct.variables.lineId,
                        })}
                />
            </div>

            <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
                <div className="flex min-w-0 flex-col gap-4">
                    {anchorContext &&
                        slots.promotionsSync?.({
                            ...anchorContext,
                            setSelectionPending: setSyncPending,
                        })}
                    {!anchor && (
                        <p role="status" className="text-[12px] text-muted-foreground">
                            {referred
                                ? 'La venta se derivó: solo cobras aquí los extras de contado.'
                                : 'Elige un teléfono del catálogo.'}
                        </p>
                    )}
                    {anchorContext && slots.accessoryDiscount?.(anchorContext)}
                    {slots.accessoryPromotions && anchorContext && (
                        <div data-testid="slot-accessory-promotions">
                            {slots.accessoryPromotions(anchorContext)}
                        </div>
                    )}
                    <ExtrasCatalog
                        items={items}
                        counts={counts}
                        loading={isLoading}
                        error={isError}
                        onRetry={() => void refetch()}
                        onAdd={(sku) => addExtra(sku)}
                        onRemove={(sku) => removeExtra(sku)}
                        onAddPiece={(sku) => addExtra(sku, false)}
                        onRemovePiece={(sku) => removeExtra(sku, false)}
                        onVariantsDone={notifyLinesChanged}
                        benefits={slots.extraBenefits}
                        showFinancing={sureKeep}
                        {...(!referred && {
                            kits,
                            onSwapKit: swapToKit,
                            swapDisabled: busy,
                            kitDiscountedPrice: (kit: CatalogItem) =>
                                slots.kitBenefits?.(
                                    rawCatalog?.items.find((i) => i.sku === kit.sku) ?? kit,
                                ).discountedPriceCents ?? null,
                        })}
                        gifts={
                            anchorContext &&
                            slots.gifts && (
                                <div data-testid="slot-gifts" className="empty:hidden">
                                    {slots.gifts({
                                        ...anchorContext,
                                        setSelectionPending: setGiftPending,
                                        onChangeInPhoneStep: () => {
                                            goToStep('product');
                                        },
                                    })}
                                </div>
                            )
                        }
                    />
                </div>

                <BuildSaleSummaryPanel
                    {...summaryProps}
                    afterSale={slots.afterSale}
                    giftName={slots.giftName}
                    promotionLabel={slots.promotionLabel}
                    variants={lineVariantsBySku(session)}
                    kitPieces={isKit ? kitPieceNamesOf(anchor, items, descriptions) : []}
                    kitCapChange={ownCapChange}
                    phonePromotions={
                        anchorContext &&
                        slots.phonePromotions?.({
                            ...anchorContext,
                            onChangeInPhoneStep: () => {
                                goToStep('product');
                            },
                        })
                    }
                    kitPromotions={
                        anchorContext &&
                        isKit &&
                        slots.kitPromotions?.({
                            ...anchorContext,
                            setSelectionPending: setSalePending,
                        })
                    }
                    onRemoveKit={removeKit}
                    onRemoveGift={removeGift}
                    onRemoveExtra={
                        referred
                            ? undefined
                            : (sku) => {
                                  void removeAllOfExtra(sku);
                              }
                    }
                    busy={busy}
                    removingKey={removingKey}
                />
            </div>

            <BuildSaleSummaryBar {...summaryProps} />

            <ConfirmDialog
                open={discardPrompt !== undefined}
                onOpenChange={(open) => {
                    if (!open) answerDiscard(false);
                }}
                title="La venta ya está cotizada"
                description={QUOTE_DISCARD_WARNING}
                confirmLabel="Cambiar y volver a cotizar"
                cancelLabel="Mejor no"
                onConfirm={() => {
                    answerDiscard(true);
                }}
            />
        </div>
    );
}
